<template>
    <div
        v-cloak
        class="row"
    >
        <div class="col-xs-12">
            <div class="box box-primary">
                <div class="box-header with-border">
                    <h3 class="box-title">{{ $t('blocked_names.filter') }}</h3>
                    <div class="box-tools pull-right">
                        <button
                            class="btn btn-box-tool"
                            title="Collapse"
                            type="button"
                            :aria-expanded="String(filterExpanded)"
                            @click="filterExpanded = !filterExpanded"
                        >
                            <i
                                :class="filterExpanded ? 'fa-minus' : 'fa-plus'"
                                class="fa"
                            ></i>
                        </button>
                    </div>
                </div>

                <vue-slide-up-down
                    :active="filterExpanded"
                    :duration="700"
                >
                    <div class="box-body">
                        <form
                            role="form"
                            @submit.prevent="applyFilter"
                        >
                            <div class="row">
                                <div class="form-group col-lg-4 col-md-6 col-xs-12">
                                    <label for="blocked-names-first-name">{{ $t('blocked_names.first_name') }}</label>
                                    <input
                                        id="blocked-names-first-name"
                                        v-model.trim="filter.first_name"
                                        :placeholder="`${$t('enter')} ${$t('blocked_names.first_name')}`"
                                        class="form-control"
                                        type="text"
                                        maxlength="255"
                                    />
                                </div>

                                <div class="form-group col-lg-4 col-md-6 col-xs-12">
                                    <label for="blocked-names-last-name">{{ $t('blocked_names.last_name') }}</label>
                                    <input
                                        id="blocked-names-last-name"
                                        v-model.trim="filter.last_name"
                                        :placeholder="`${$t('enter')} ${$t('blocked_names.last_name')}`"
                                        class="form-control"
                                        type="text"
                                        maxlength="255"
                                    />
                                </div>

                                <div class="form-group col-lg-4 col-md-12 col-xs-12">
                                    <label class="col-xs-12 hidden-xs">&nbsp;</label>
                                    <button
                                        class="btn btn-sm btn-primary"
                                        type="submit"
                                        :disabled="loading"
                                    >
                                        {{ $t('blocked_names.apply') }}
                                    </button>
                                    <button
                                        class="btn btn-sm btn-info"
                                        type="button"
                                        :disabled="loading"
                                        @click="clearFilter"
                                    >
                                        {{ $t('blocked_names.clear') }}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </vue-slide-up-down>
            </div>

            <div class="box box-primary">
                <div class="box-header with-border">
                    <h3 class="box-title">{{ $t('blocked_names.table_title') }}</h3>

                    <div class="box-tools pull-right">
                        <BadgeTotal
                            :value="paginationOptions.total"
                            color="gray"
                            :label="`${$t('total')}: `"
                        />
                    </div>
                </div>

                <div class="box-body table-responsive no-padding">
                    <table class="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>{{ $t('id') }}</th>
                                <th>{{ $t('blocked_names.first_name') }}</th>
                                <th>{{ $t('blocked_names.last_name') }}</th>
                                <th class="text-right">{{ $t('blocked_names.created_at') }}</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-if="loading">
                                <td colspan="4">{{ $t('blocked_names.loading') }}</td>
                            </tr>
                            <tr v-else-if="listFailed">
                                <td
                                    colspan="4"
                                    class="text-danger"
                                    role="alert"
                                >
                                    {{ $t('blocked_names.unavailable') }}
                                </td>
                            </tr>
                            <tr v-else-if="!blockedNames.length">
                                <td colspan="4">
                                    {{ isFiltered ? $t('blocked_names.no_matches') : $t('blocked_names.no_blocked_names') }}
                                </td>
                            </tr>
                            <template v-else>
                                <tr
                                    v-for="blockedName in blockedNames"
                                    :key="blockedName.id"
                                >
                                    <td>{{ blockedName.id }}</td>
                                    <td>{{ blockedName.first_name }}</td>
                                    <td>{{ blockedName.last_name }}</td>
                                    <td class="text-right">{{ blockedName.created_at }}</td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>

                <div
                    v-if="paginationOptions.last_page > 1"
                    class="box-footer clearfix"
                >
                    <PaginationTable
                        :options="paginationOptions"
                        @change-page="fetchBlockedNames"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import PaginationTable from '@/components/subcomponents/PaginationTable';

export default {
    components: {
        BadgeTotal,
        PaginationTable,
    },
    data() {
        return {
            blockedNames: [],
            filter: {
                first_name: '',
                last_name: '',
            },
            appliedFilter: {},
            filterExpanded: false,
            paginationOptions: {
                disabled: true,
                current_page: 1,
                last_page: 1,
                total: 0,
            },
            loading: false,
            listFailed: false,
            requestGeneration: 0,
        };
    },
    computed: {
        isFiltered() {
            return Object.keys(this.appliedFilter).length > 0;
        },
    },
    mounted() {
        this.fetchBlockedNames();
    },
    beforeDestroy() {
        this.requestGeneration++;
    },
    methods: {
        applyFilter() {
            this.appliedFilter = Object.keys(this.filter).reduce((applied, key) => {
                if (this.filter[key] !== '') {
                    applied[key] = this.filter[key];
                }

                return applied;
            }, {});
            this.fetchBlockedNames();
        },
        clearFilter() {
            this.filter = { first_name: '', last_name: '' };
            this.applyFilter();
        },
        async fetchBlockedNames(page = 1) {
            const generation = ++this.requestGeneration;

            this.loading = true;
            this.listFailed = false;
            this.paginationOptions.disabled = true;

            try {
                const {
                    data: {
                        blocked_names: { data: blockedNames, ...paginationOptions },
                    },
                } = await axios.get('/contacts/blocked-names/data', { params: { ...this.appliedFilter, page } });

                if (generation !== this.requestGeneration) {
                    return;
                }

                // The requested page can disappear when rows are removed between requests.
                if (paginationOptions.current_page > paginationOptions.last_page) {
                    await this.fetchBlockedNames(paginationOptions.last_page);
                    return;
                }

                this.blockedNames = blockedNames;
                this.paginationOptions = { ...paginationOptions, disabled: false };
            } catch (error) {
                if (generation === this.requestGeneration) {
                    this.blockedNames = [];
                    this.listFailed = true;
                    this.paginationOptions = { disabled: true, current_page: 1, last_page: 1, total: 0 };
                }
            } finally {
                if (generation === this.requestGeneration) {
                    this.loading = false;
                }
            }
        },
    },
};
</script>
